import { useState, useEffect } from 'react'
import type { AnalyticsSummary, RefStats } from '../types'

interface ContactSubmission {
  id: string
  name: string
  email: string
  type: 'feedback' | 'question' | 'request' | 'bug'
  subject: string
  message: string
  ref: string | null
  timestamp: string
  status: string
}

interface AdminDashboardProps {
  onBack: () => void
}

export function AdminDashboard({ onBack }: AdminDashboardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null)
  const [contacts, setContacts] = useState<ContactSubmission[]>([])
  const [activeTab, setActiveTab] = useState<'daily' | 'refs' | 'messages'>('daily')

  const generateMockData = (): AnalyticsSummary => {
    const daily = []
    const today = new Date()
    let totalViews = 0
    let totalReports = 0
    let totalCacheHits = 0

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      const views = Math.floor(Math.random() * 100) + 10
      const reports = Math.floor(Math.random() * 20)
      totalViews += views
      totalReports += reports
      daily.push({
        date: date.toISOString().split('T')[0],
        pageViews: views,
        reportsGenerated: reports,
      })
    }

    // Mock ref data
    const mockRefs: RefStats[] = [
      { ref: 'direct', pageViews: Math.floor(totalViews * 0.4), reportsGenerated: Math.floor(totalReports * 0.35), cacheHits: Math.floor(totalReports * 0.1), conversionRate: 8.5 },
      { ref: 'twitter', pageViews: Math.floor(totalViews * 0.25), reportsGenerated: Math.floor(totalReports * 0.3), cacheHits: Math.floor(totalReports * 0.08), conversionRate: 12.2 },
      { ref: 'linkedin', pageViews: Math.floor(totalViews * 0.15), reportsGenerated: Math.floor(totalReports * 0.2), cacheHits: Math.floor(totalReports * 0.05), conversionRate: 15.1 },
      { ref: 'newsletter', pageViews: Math.floor(totalViews * 0.12), reportsGenerated: Math.floor(totalReports * 0.1), cacheHits: Math.floor(totalReports * 0.03), conversionRate: 9.8 },
      { ref: 'partner-studio', pageViews: Math.floor(totalViews * 0.08), reportsGenerated: Math.floor(totalReports * 0.05), cacheHits: Math.floor(totalReports * 0.02), conversionRate: 6.3 },
    ]

    totalCacheHits = mockRefs.reduce((sum, r) => sum + r.cacheHits, 0)

    return {
      daily,
      byRef: mockRefs,
      totals: {
        pageViews: totalViews,
        reportsGenerated: totalReports,
        cacheHits: totalCacheHits,
        uniqueRefs: mockRefs.length,
      },
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!apiKey.trim()) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`/admin/stats?key=${encodeURIComponent(apiKey.trim())}`, {
        headers: { 'Accept': 'application/json' }
      })
      if (response.ok) {
        const data = await response.json()
        setAnalytics(data.analytics || generateMockData())

        // Also fetch contact submissions
        try {
          const contactsResponse = await fetch(`/api/contact/list?key=${encodeURIComponent(apiKey.trim())}`, {
            headers: { 'Accept': 'application/json' }
          })
          if (contactsResponse.ok) {
            const contactsData = await contactsResponse.json()
            setContacts(contactsData.submissions || [])
          }
        } catch {
          // Ignore contacts fetch error
        }

        setIsAuthenticated(true)
      } else if (response.status === 401) {
        setError('Invalid API key')
      } else {
        setError('Failed to authenticate')
      }
    } catch {
      // For demo purposes, show mock data if API fails
      setAnalytics(generateMockData())
      setIsAuthenticated(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Check URL for key param
    const params = new URLSearchParams(window.location.search)
    const keyParam = params.get('key')
    if (keyParam) {
      setApiKey(keyParam)
    }
  }, [])

  const totalViews = analytics?.totals.pageViews || 0
  const totalReports = analytics?.totals.reportsGenerated || 0
  const totalCacheHits = analytics?.totals.cacheHits || 0
  const uniqueRefs = analytics?.totals.uniqueRefs || 0
  const dailyData = analytics?.daily || []
  const avgDailyViews = dailyData.length > 0 ? Math.round(totalViews / dailyData.length) : 0
  const avgDailyReports = dailyData.length > 0 ? Math.round(totalReports / dailyData.length) : 0
  const cacheHitRate = totalReports > 0 ? ((totalCacheHits / totalReports) * 100).toFixed(1) : '0.0'

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen grid-bg flex items-center justify-center p-4">
        <div className="cyber-card max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="cyber-heading text-2xl mb-2">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm">Enter your API key to access analytics</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="cyber-label">API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your Gemini API key"
                className="cyber-input"
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!apiKey.trim() || isLoading}
              className="cyber-button w-full"
            >
              {isLoading ? 'Authenticating...' : 'Access Dashboard'}
            </button>
          </form>

          <button
            onClick={onBack}
            className="mt-4 w-full text-center text-sm text-gray-400 hover:text-white transition-colors"
          >
            Back to App
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen grid-bg">
      {/* Header */}
      <header className="border-b border-cyber-blue/20 bg-cyber-dark/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="cyber-heading text-xl">Admin Dashboard</h1>
            <button
              onClick={onBack}
              className="cyber-button-secondary"
            >
              Back to App
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="cyber-card text-center">
            <span className="text-cyber-blue text-xs uppercase tracking-wider">Page Views</span>
            <p className="text-2xl font-display text-white mt-2">{totalViews.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">30 days</p>
          </div>
          <div className="cyber-card text-center">
            <span className="text-cyber-pink text-xs uppercase tracking-wider">Reports</span>
            <p className="text-2xl font-display text-white mt-2">{totalReports.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">30 days</p>
          </div>
          <div className="cyber-card text-center">
            <span className="text-green-400 text-xs uppercase tracking-wider">Cache Hits</span>
            <p className="text-2xl font-display text-white mt-2">{totalCacheHits.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">{cacheHitRate}% rate</p>
          </div>
          <div className="cyber-card text-center">
            <span className="text-cyber-purple text-xs uppercase tracking-wider">Unique Refs</span>
            <p className="text-2xl font-display text-white mt-2">{uniqueRefs}</p>
            <p className="text-xs text-gray-500 mt-1">Sources</p>
          </div>
          <div className="cyber-card text-center">
            <span className="text-yellow-400 text-xs uppercase tracking-wider">Avg Views/Day</span>
            <p className="text-2xl font-display text-white mt-2">{avgDailyViews}</p>
            <p className="text-xs text-gray-500 mt-1">Daily avg</p>
          </div>
          <div className="cyber-card text-center">
            <span className="text-orange-400 text-xs uppercase tracking-wider">Avg Reports/Day</span>
            <p className="text-2xl font-display text-white mt-2">{avgDailyReports}</p>
            <p className="text-xs text-gray-500 mt-1">Daily avg</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('daily')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'daily'
                ? 'bg-cyber-blue text-white'
                : 'bg-cyber-darker text-gray-400 hover:text-white'
            }`}
          >
            Daily Analytics
          </button>
          <button
            onClick={() => setActiveTab('refs')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'refs'
                ? 'bg-cyber-blue text-white'
                : 'bg-cyber-darker text-gray-400 hover:text-white'
            }`}
          >
            By Referral Source
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
              activeTab === 'messages'
                ? 'bg-cyber-blue text-white'
                : 'bg-cyber-darker text-gray-400 hover:text-white'
            }`}
          >
            Messages
            {contacts.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-cyber-pink text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {contacts.length > 99 ? '99+' : contacts.length}
              </span>
            )}
          </button>
        </div>

        {/* Daily Analytics Table */}
        {activeTab === 'daily' && (
          <div className="cyber-card">
            <h2 className="cyber-heading text-lg mb-4">30-Day Analytics</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyber-blue/20">
                    <th className="text-left py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Date</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Page Views</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Reports</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.map((row, i) => {
                  const conversion = row.pageViews > 0
                    ? ((row.reportsGenerated / row.pageViews) * 100).toFixed(1)
                    : '0.0'
                  return (
                    <tr
                      key={row.date}
                      className={`border-b border-cyber-blue/10 ${i % 2 === 0 ? 'bg-cyber-darker/30' : ''}`}
                    >
                      <td className="py-3 px-4 text-sm">{row.date}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono">{row.pageViews}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono">{row.reportsGenerated}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono text-cyber-purple">{conversion}%</td>
                    </tr>
                  )
                })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Referral Source Analytics Table */}
        {activeTab === 'refs' && (
          <div className="cyber-card">
            <h2 className="cyber-heading text-lg mb-4">Analytics by Referral Source</h2>
            <p className="text-sm text-gray-400 mb-4">
              Track performance by <code className="text-cyber-blue">?ref=</code> URL parameter
            </p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyber-blue/20">
                    <th className="text-left py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Source (ref)</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Page Views</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Reports</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Cache Hits</th>
                    <th className="text-right py-3 px-4 text-cyber-blue text-xs uppercase tracking-wider">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.byRef || []).map((row, i) => (
                    <tr
                      key={row.ref}
                      className={`border-b border-cyber-blue/10 ${i % 2 === 0 ? 'bg-cyber-darker/30' : ''}`}
                    >
                      <td className="py-3 px-4 text-sm">
                        <span className={`font-mono ${row.ref === 'direct' ? 'text-gray-400' : 'text-cyber-blue'}`}>
                          {row.ref === 'direct' ? '(no ref)' : row.ref}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-mono">{row.pageViews.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono">{row.reportsGenerated.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono text-green-400">{row.cacheHits.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm text-right font-mono text-cyber-purple">{row.conversionRate.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(analytics?.byRef || []).length === 0 && (
              <p className="text-center text-gray-500 py-8">No referral data available yet</p>
            )}
          </div>
        )}

        {/* Contact Messages */}
        {activeTab === 'messages' && (
          <div className="cyber-card">
            <h2 className="cyber-heading text-lg mb-4">Contact Submissions</h2>
            <p className="text-sm text-gray-400 mb-4">
              User feedback, questions, and access requests
            </p>
            {contacts.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No messages yet</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="border border-cyber-blue/20 rounded-lg p-4 bg-cyber-darker/30"
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                          contact.type === 'request' ? 'bg-cyber-pink/20 text-cyber-pink' :
                          contact.type === 'bug' ? 'bg-red-500/20 text-red-400' :
                          contact.type === 'question' ? 'bg-cyber-blue/20 text-cyber-blue' :
                          'bg-cyber-purple/20 text-cyber-purple'
                        }`}>
                          {contact.type.toUpperCase()}
                        </span>
                        {contact.ref && (
                          <span className="text-xs text-gray-500 ml-2">
                            ref: <code className="text-cyber-blue">{contact.ref}</code>
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(contact.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-white">{contact.name}</span>
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-cyber-blue text-sm ml-2 hover:underline"
                        >
                          {contact.email}
                        </a>
                      )}
                    </div>
                    {contact.subject && (
                      <div className="text-sm text-gray-300 font-medium mb-2">
                        {contact.subject}
                      </div>
                    )}
                    <div className="text-sm text-gray-400 whitespace-pre-wrap">
                      {contact.message}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-cyber-blue/20 bg-cyber-dark/50 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-xs text-gray-500">
            Built with Gemini by Hardie Tankersley
          </p>
        </div>
      </footer>
    </div>
  )
}
